import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  QrCode2 as QrCodeIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { format, parseISO, isValid } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '@/services/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectBankAccount } from '@/store/slices/bankAccountSlice';
import type { Cobranca } from '@/types/bank';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.error?.message || e?.error?.message || e?.message || fallback;

const formatCurrency = (value: number | null | undefined) => {
  if (value === undefined || value === null || isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  const d = parseISO(value);
  if (isValid(d)) return format(d, 'dd/MM/yyyy HH:mm');
  const d2 = new Date(value);
  return isNaN(d2.getTime()) ? value : format(d2, 'dd/MM/yyyy HH:mm');
};

const copy = (text: string, label = 'Copiado') => {
  navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(() => toast.error('Não foi possível copiar'));
};

/** "1.234,56" | "1234.56" | "1234,56" → 1234.56 */
const parseValor = (s: string): number => {
  const clean = (s || '').trim().replace(/[^\d,.]/g, '');
  if (!clean) return NaN;
  if (/,\d{1,2}$/.test(clean)) return parseFloat(clean.replace(/\./g, '').replace(',', '.'));
  return parseFloat(clean.replace(/,/g, ''));
};

const formatDocumento = (doc: string | null | undefined) => {
  if (!doc) return '';
  const n = doc.replace(/\D/g, '');
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

const STATUS_PAGO = new Set(['CONCLUIDA', 'CONCLUÍDA', 'PAGA', 'PAGO', 'LIQUIDADA', 'COMPLETED']);
const STATUS_ABERTA = new Set(['ATIVA', 'ATIVO', 'PENDENTE', 'ACTIVE']);

const statusChip = (status: string | undefined) => {
  const s = (status || '').toUpperCase();
  if (STATUS_PAGO.has(s)) return <Chip size="small" color="success" label={status} />;
  if (STATUS_ABERTA.has(s)) return <Chip size="small" color="primary" variant="outlined" label={status} />;
  if (!s) return <Chip size="small" variant="outlined" label="—" />;
  return <Chip size="small" color="default" variant="outlined" label={status} />;
};

interface FormState {
  valor: string;
  descricao: string;
  devedorNome: string;
  devedorDocumento: string;
  expiracaoSegundos: string;
}

const FORM_INICIAL: FormState = {
  valor: '',
  descricao: '',
  devedorNome: '',
  devedorDocumento: '',
  expiracaoSegundos: '3600',
};

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function PixCobrancaPage() {
  const account = useAppSelector(selectBankAccount);

  const [form, setForm] = useState<FormState>(FORM_INICIAL);
  const [criando, setCriando] = useState(false);
  const [cobranca, setCobranca] = useState<Cobranca | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Consulta por txid (da cobrança criada ou digitado)
  const [txidConsulta, setTxidConsulta] = useState('');
  const [consultando, setConsultando] = useState(false);
  const [consultada, setConsultada] = useState<Cobranca | null>(null);
  const [consultadaEm, setConsultadaEm] = useState<Date | null>(null);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const valorNumero = useMemo(() => parseValor(form.valor), [form.valor]);
  const formErro = useMemo(() => {
    if (!form.valor.trim()) return null;
    if (isNaN(valorNumero) || valorNumero <= 0) return 'Informe um valor maior que zero.';
    const docDigits = form.devedorDocumento.replace(/\D/g, '');
    if (form.devedorDocumento && ![11, 14].includes(docDigits.length)) return 'Documento do devedor deve ser CPF (11 dígitos) ou CNPJ (14 dígitos).';
    if ((form.devedorNome && !form.devedorDocumento) || (!form.devedorNome && form.devedorDocumento)) {
      return 'Para identificar o devedor, informe nome e CPF/CNPJ juntos.';
    }
    const exp = Number(form.expiracaoSegundos);
    if (!Number.isInteger(exp) || exp < 60) return 'Expiração mínima: 60 segundos.';
    return null;
  }, [form, valorNumero]);

  const criarCobranca = async () => {
    if (!form.valor.trim()) return toast.error('Informe o valor');
    if (formErro) return toast.error(formErro);
    setCriando(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        valor: Number(valorNumero.toFixed(2)),
        expiracaoSegundos: Number(form.expiracaoSegundos) || 3600,
      };
      if (form.descricao.trim()) body.descricao = form.descricao.trim();
      if (form.devedorNome.trim() && form.devedorDocumento.trim()) {
        body.devedor = { nome: form.devedorNome.trim(), documento: form.devedorDocumento.replace(/\D/g, '') };
      }
      const response = await api.post<Cobranca>('/bank/pix/cobranca', body);
      if (response.success && response.data) {
        setCobranca(response.data);
        setConsultada(null);
        setConsultadaEm(null);
        setTxidConsulta(response.data.txid);
        toast.success('Cobrança criada');
      } else {
        setError(response.error?.message || 'Erro ao criar cobrança');
      }
    } catch (err: any) {
      setError(errMsg(err, 'Erro ao criar cobrança'));
    } finally {
      setCriando(false);
    }
  };

  const consultarCobranca = async (txid?: string) => {
    const id = (txid ?? txidConsulta).trim();
    if (!id) return toast.error('Informe o txid');
    setConsultando(true);
    setError(null);
    try {
      const response = await api.get<Cobranca>(`/bank/pix/cobranca/${encodeURIComponent(id)}`);
      if (response.success && response.data) {
        setConsultada(response.data);
        setConsultadaEm(new Date());
        // Se é a cobrança que acabou de ser criada, atualiza o status dela também
        if (cobranca && cobranca.txid === response.data.txid) {
          setCobranca(prev => (prev ? { ...prev, ...response.data } : prev));
        }
        const s = (response.data.status || '').toUpperCase();
        if (STATUS_PAGO.has(s)) toast.success('Cobrança paga');
        else toast(`Status: ${response.data.status || 'desconhecido'}`);
      } else {
        setError(response.error?.message || 'Erro ao consultar cobrança');
      }
    } catch (err: any) {
      setError(errMsg(err, 'Erro ao consultar cobrança'));
    } finally {
      setConsultando(false);
    }
  };

  const novaCobranca = () => {
    setCobranca(null);
    setConsultada(null);
    setConsultadaEm(null);
    setForm(FORM_INICIAL);
    setError(null);
  };

  const renderQr = (c: Cobranca) => {
    if (c.imagemQrcode) {
      return (
        <Box component="img" src={c.imagemQrcode} alt="QR Code Pix"
          sx={{ width: 220, height: 220, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: '#fff', p: 0.5 }} />
      );
    }
    if (c.pixCopiaECola) {
      return (
        <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff', lineHeight: 0 }}>
          <QRCodeSVG value={c.pixCopiaECola} size={204} level="M" includeMargin={false} />
        </Box>
      );
    }
    return (
      <Box sx={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">QR indisponível</Typography>
      </Box>
    );
  };

  const renderDetalhesCobranca = (c: Cobranca, titulo: string) => {
    const pago = STATUS_PAGO.has((c.status || '').toUpperCase());
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            <QrCodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {titulo}
          </Typography>
          {statusChip(c.status)}
        </Box>

        {pago && (
          <Alert severity="success" sx={{ mb: 2 }}>Esta cobrança foi paga.</Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              {renderQr(c)}
              <Typography variant="h5" fontWeight={700}>{formatCurrency(Number(c.valor))}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm>
            <Stack spacing={2}>
              <TextField
                label="Pix copia e cola"
                value={c.pixCopiaECola || ''}
                fullWidth
                multiline
                minRows={3}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: 12 },
                  endAdornment: c.pixCopiaECola ? (
                    <InputAdornment position="end">
                      <Tooltip title="Copiar código">
                        <IconButton size="small" onClick={() => copy(c.pixCopiaECola, 'Código Pix copiado')}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              <Button variant="contained" startIcon={<CopyIcon />} disabled={!c.pixCopiaECola}
                onClick={() => copy(c.pixCopiaECola, 'Código Pix copiado')} sx={{ alignSelf: 'flex-start' }}>
                Copiar código
              </Button>

              <Divider />

              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">txid</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{c.txid}</Typography>
                    <Tooltip title="Copiar txid">
                      <IconButton size="small" onClick={() => copy(c.txid, 'txid copiado')}>
                        <CopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body2" fontWeight={500}>{c.status || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Criada em</Typography>
                  <Typography variant="body2">{formatDateTime(c.criadoEm)}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Expira em</Typography>
                  <Typography variant="body2">{formatDateTime(c.expiraEm)}</Typography>
                </Grid>
              </Grid>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Cobrança Pix (QR Code)</Typography>
          <Typography variant="body2" color="text.secondary">
            Gere um QR Code dinâmico para receber na conta selecionada
          </Typography>
          {account && (
            <Chip
              size="small"
              variant="outlined"
              icon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
              label={`${account.providerLabel} · ${account.label}${account.companyName ? ` — ${account.companyName}` : ''}`}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        {cobranca && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={novaCobranca}>
            Nova cobrança
          </Button>
        )}
      </Box>

      {!account && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nenhuma conta bancária selecionada. Escolha uma conta no seletor do topo da página
          (Empresa · Banco). Sem seleção, o sistema usa a conta padrão da empresa.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Formulário / resultado */}
        <Grid item xs={12} md={cobranca ? 12 : 7}>
          {cobranca ? (
            <Stack spacing={3}>
              {renderDetalhesCobranca(cobranca, 'Cobrança gerada')}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={consultando ? <CircularProgress size={16} /> : <RefreshIcon />}
                  disabled={consultando} onClick={() => consultarCobranca(cobranca.txid)}>
                  Verificar pagamento
                </Button>
                <Button variant="text" startIcon={<AddIcon />} onClick={novaCobranca}>
                  Nova cobrança
                </Button>
                {consultadaEm && consultada?.txid === cobranca.txid && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Verificado às {format(consultadaEm, 'HH:mm:ss')}
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Nova cobrança</Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valor"
                    value={form.valor}
                    onChange={set('valor')}
                    fullWidth
                    size="small"
                    required
                    placeholder="0,00"
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    helperText={form.valor && !isNaN(valorNumero) ? formatCurrency(valorNumero) : ' '}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Expiração (segundos)"
                    value={form.expiracaoSegundos}
                    onChange={set('expiracaoSegundos')}
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 60, step: 60 }}
                    helperText={(() => {
                      const s = Number(form.expiracaoSegundos);
                      if (!s || s < 60) return ' ';
                      if (s % 86400 === 0) return `${s / 86400} dia(s)`;
                      if (s % 3600 === 0) return `${s / 3600} hora(s)`;
                      if (s % 60 === 0) return `${s / 60} minuto(s)`;
                      return ' ';
                    })()}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descrição para o pagador"
                    value={form.descricao}
                    onChange={set('descricao')}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 140 }}
                    helperText={`${form.descricao.length}/140 — aparece no app do pagador (opcional)`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Devedor (opcional)</Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField label="Nome" value={form.devedorNome} onChange={set('devedorNome')} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField label="CPF / CNPJ" value={form.devedorDocumento} onChange={set('devedorDocumento')}
                    fullWidth size="small" inputProps={{ inputMode: 'numeric' }}
                    helperText={form.devedorDocumento ? formatDocumento(form.devedorDocumento) : ' '} />
                </Grid>
                {formErro && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ py: 0 }}>{formErro}</Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button variant="contained" onClick={criarCobranca} disabled={criando || !form.valor.trim() || !!formErro}
                    startIcon={criando ? <CircularProgress size={18} color="inherit" /> : <QrCodeIcon />}>
                    {criando ? 'Gerando...' : 'Gerar QR Code'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Grid>

        {/* Consultar por txid */}
        <Grid item xs={12} md={cobranca ? 12 : 5}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Consultar cobrança
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Informe o txid para verificar se a cobrança foi paga.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="txid"
                value={txidConsulta}
                onChange={(e) => setTxidConsulta(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') consultarCobranca(); }}
                fullWidth
                size="small"
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
              <Button variant="outlined" onClick={() => consultarCobranca()} disabled={consultando || !txidConsulta.trim()}
                startIcon={consultando ? <CircularProgress size={16} /> : <SearchIcon />} sx={{ whiteSpace: 'nowrap' }}>
                Consultar
              </Button>
            </Stack>

            {consultada && consultada.txid !== cobranca?.txid && (
              <Box sx={{ mt: 3 }}>
                {renderDetalhesCobranca(consultada, 'Resultado da consulta')}
                {consultadaEm && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Consultado às {format(consultadaEm, 'HH:mm:ss')}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
